import dayjs from 'dayjs';
import { NfcCard, Role, UserIdentityProvider, Visitor } from 'graphql/generated/graphqlTypes';
import { Translation } from 'hooks/useTranslation/useTranslation';
import { uniq } from 'lodash';
import { getCardsByVisitorId } from 'utils/NfcCard/nfcCard';
import { VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';

export const getVisitorMobileAccessAppStatus = (visitor: Visitor) =>
  visitor.mobileDevices.find((mobileDevice) => mobileDevice.role === 'ACCESS');

export const getVisitorMobileConfigAppStatus = (visitor: Visitor) =>
  visitor.mobileDevices.find((mobileDevice) => mobileDevice.role === 'CONFIG');

export const isVisitorHasCard = (visitor: Visitor, cards: NfcCard[]) =>
  getCardsByVisitorId(visitor.id, cards).length > 0;
export const isVisitorHasNoCards = (visitor: Visitor, cards: NfcCard[]) =>
  getCardsByVisitorId(visitor.id, cards).length === 0;

export const getVisitorRoles = (visitor: Visitor) => {
  const membershipRoles = visitor.visitorGroupMembership.map((membership) => membership.role);
  const isVisitorAdmin = visitor.isAdmin;
  const isVisitorInstaller = isVisitorHasInstallerRole(visitor);

  return [...membershipRoles, ...(isVisitorAdmin ? ['ADMIN'] : []), ...(isVisitorInstaller ? ['INSTALLER'] : [])];
};

export const getUniqueVisitorRoles = (visitor: Visitor) => uniq(getVisitorRoles(visitor));
export const getUniqueVisitorRolesWithoutInternalRolesAndExtGroupUsers = (visitor: Visitor) =>
  getUniqueVisitorRoles(visitor).filter((role) => role !== Role.None && role !== Role.ExternalGroupUser);

export const translateRole = (role: string, translation: Translation) => {
  switch (role) {
    case 'ADMIN':
      return translation.administrator;
    case 'EXTERNAL_GROUP_USER':
      return translation.user;
    case 'EXTERNAL_GROUP_USER_MANAGER':
      return translation.key_manager;
    case 'INSTALLER':
      return translation.installer;
    default:
      return role;
  }
};

export const isVisitorHasKeyManagerRole = (visitor: Visitor) =>
  getVisitorRoles(visitor).some((role) => role === Role.ExternalGroupUserManager);
export const isVisitorHasAdministratorRole = (visitor: Visitor) => visitor.isAdmin;
export const isVisitorHasInstallerRole = (visitor: Visitor) =>
  visitor.visitorGroupMembership.some((membership) => membership.visitorGroup.type === VisitorGroupType.Installers);

export const isVisitorHasEntraIdOrigin = (visitor: Visitor) => visitor.identityProvider === UserIdentityProvider.Entra;
export const isVisitorHasExternalOrigin = (visitor: Visitor) =>
  visitor.identityProvider === UserIdentityProvider.Cognito;

export const isVisitorHasKeySharingOrigin = (visitor: Visitor) => Boolean(visitor.email === null);

export const isVisitorExpiresIn = (visitor: Visitor, value: number, unit?: dayjs.ManipulateType) =>
  Boolean(
    visitor.expirationDate && dayjs(visitor.expirationDate).isBetween(dayjs(), dayjs().add(value, unit), 'day', '[]')
  );

export const getVisitorStatuses = (visitor: Visitor, cards: NfcCard[]): Statuses | undefined => {
  if (getVisitorMobileConfigAppStatus(visitor)?.isRegistered === false) return Statuses.isConfigDeviceDisconnected;
  if (!getVisitorMobileAccessAppStatus(visitor)?.isRegistered && isVisitorHasNoCards(visitor, cards))
    return Statuses.isAccessDeviceDisconnectedAndVisitorHasNoCards;
  if (!getVisitorMobileAccessAppStatus(visitor)?.isRegistered && !isVisitorHasNoCards(visitor, cards))
    return Statuses.isAccessDeviceDisconnectedAndVisitorHasCards;
  if (isVisitorExpiresIn(visitor, 24, 'h')) return Statuses.isVisitorExpireIn24H;
  if (isVisitorExpiresIn(visitor, 1, 'week')) return Statuses.isVisitorExpireIn1Week;
};

export const isVisitorHasOnlyInstallerMembership = (visitor: Visitor) =>
  !visitor?.visitorGroupMembership.some((group) => group.visitorGroup.type !== VisitorGroupType.Installers);

export const isVisitorHasOnlyAdministratorMembership = (visitor: Visitor) =>
  !visitor?.visitorGroupMembership.some((group) => group.visitorGroup.type !== VisitorGroupType.Administrators);

export const getVisibleVisitorGroupMemberships = (visitor: Visitor) =>
  visitor.visitorGroupMembership.filter(
    (membership) =>
      membership.visitorGroup.type !== VisitorGroupType.Administrators &&
      membership.visitorGroup.type !== VisitorGroupType.AtlasInternal
  );

export const isVisitorWithoutGroup = (visitor: Visitor) => getVisibleVisitorGroupMemberships(visitor).length === 0;

export enum Statuses {
  isConfigDeviceDisconnected,
  isAccessDeviceDisconnectedAndVisitorHasNoCards,
  isAccessDeviceDisconnectedAndVisitorHasCards,
  isVisitorExpireIn24H,
  isVisitorExpireIn1Week
}

export const getStatusPriority = (visitor: Visitor, cards: NfcCard[]) => {
  const statuses = getVisitorStatuses(visitor, cards);
  switch (statuses) {
    case Statuses.isConfigDeviceDisconnected:
      return 5;
    case Statuses.isAccessDeviceDisconnectedAndVisitorHasNoCards:
      return 4;
    case Statuses.isAccessDeviceDisconnectedAndVisitorHasCards:
      return 3;
    case Statuses.isVisitorExpireIn24H:
      return 2;
    case Statuses.isVisitorExpireIn1Week:
      return 1;
    default:
      return 0;
  }
};

export const getVisitorMembershipByVisitorGroupId = (visitor: Visitor, visitorGroupId: string) =>
  visitor.visitorGroupMembership.find(
    (visitorGroupMembership) => visitorGroupMembership.visitorGroup.id === visitorGroupId
  );

// The user is only deleted once all their memberships expire. If any membership has no expiration
// date the user has an "other" (permanent) membership and is never deleted, so no date is shown.
// Otherwise the user is deleted at the last (latest) membership expiration date.
export const getUserDeletionDate = (visitor: Visitor): string | null => {
  const memberships = visitor.visitorGroupMembership;
  if (memberships.length === 0 || memberships.some((membership) => !membership.expirationDate)) return null;
  return memberships.reduce<string | null>(
    (latest, membership) =>
      !latest || dayjs(membership.expirationDate).isAfter(dayjs(latest)) ? membership.expirationDate! : latest,
    null
  );
};
